/**
 * Whitelist of trustworthy leagues / competitions for live picks.
 *
 * Norsk Tipping's `league` strings come in mixed Norwegian/English forms
 * (e.g. "Norge - Eliteserien", "Internasjonal klubb - UEFA Europa League",
 * "Italia - Serie C, opprykkssluttspill", "Algerie - Ligue 1"). The format
 * is not strictly Country-Competition or Competition-Country — both orders
 * appear in the wild.
 *
 * A pattern matches when:
 *   - at least one `countryHints` substring appears in the raw league
 *     (case-insensitive, accent-stripped), AND
 *   - the `competitionHint` substring appears, AND
 *   - no `excludes` substring appears (per-pattern + universal).
 *
 * Universal excludes catch women's, youth and reserve competitions where
 * pricing is noisy and our backtest data does not cover them.
 */

export type LeagueId =
  | "EPL"
  | "ENG_CHAMPIONSHIP"
  | "FA_CUP"
  | "EFL_CUP"
  | "LA_LIGA"
  | "COPA_DEL_REY"
  | "SERIE_A"
  | "COPPA_ITALIA"
  | "BUNDESLIGA"
  | "DFB_POKAL"
  | "LIGUE_1"
  | "COUPE_DE_FRANCE"
  | "EREDIVISIE"
  | "PRIMEIRA_LIGA"
  | "SCO_PREMIERSHIP"
  | "ELITESERIEN"
  | "NM_CUP"
  | "UCL"
  | "UEL"
  | "UECL"
  | "UEFA_NATIONS_LEAGUE"
  | "UEFA_SUPER_CUP"
  | "EUROS"
  | "WORLD_CUP"
  | "COPA_AMERICA"
  | "AFCON";

export type LeaguePattern = {
  id: LeagueId;
  label: string;
  countryHints: string[];
  competitionHint: string;
  alternateCompetitionHints?: string[];
  excludes?: string[];
};

const UNIVERSAL_EXCLUDES: ReadonlyArray<string> = [
  "kvinner",
  "damer",
  "kvinnor",
  "frauen",
  "femminile",
  "femenina",
  "féminin",
  "feminin",
  "women",
  "ladies",
  "u23",
  "u-23",
  "u22",
  "u-22",
  "u21",
  "u-21",
  "u20",
  "u-20",
  "u19",
  "u-19",
  "u18",
  "u-18",
  "u17",
  "u-17",
  "youth",
  "junior",
  "ungdom",
  "reserve",
  "reserves",
  "b-team",
  "amatør",
  "amateur",
  "akademi",
];

export const WHITELIST: ReadonlyArray<LeaguePattern> = [
  // England
  {
    id: "EPL",
    label: "Premier League",
    countryHints: ["england", "engelsk", "britain"],
    competitionHint: "premier league",
    excludes: ["national league", "championship", "league one", "league two"],
  },
  {
    id: "ENG_CHAMPIONSHIP",
    label: "EFL Championship",
    countryHints: ["england", "engelsk", "britain"],
    competitionHint: "championship",
    excludes: ["premier", "scottish", "league one", "league two", "national"],
  },
  {
    id: "FA_CUP",
    label: "FA Cup",
    countryHints: ["england", "engelsk", "britain"],
    competitionHint: "fa cup",
    alternateCompetitionHints: ["facup"],
    excludes: ["community shield"],
  },
  {
    id: "EFL_CUP",
    label: "EFL Cup",
    countryHints: ["england", "engelsk", "britain"],
    competitionHint: "efl cup",
    alternateCompetitionHints: ["carabao cup", "league cup", "ligacupen"],
  },

  // Spain
  {
    id: "LA_LIGA",
    label: "La Liga",
    countryHints: ["spania", "spain", "espa"],
    competitionHint: "la liga",
    excludes: ["segunda", "primera federacion", "2"],
  },
  {
    id: "COPA_DEL_REY",
    label: "Copa del Rey",
    countryHints: ["spania", "spain", "espa"],
    competitionHint: "copa del rey",
  },

  // Italy
  {
    id: "SERIE_A",
    label: "Serie A",
    countryHints: ["italia", "italy"],
    competitionHint: "serie a",
    excludes: ["serie a2", "serie b", "serie c", "serie d"],
  },
  {
    id: "COPPA_ITALIA",
    label: "Coppa Italia",
    countryHints: ["italia", "italy"],
    competitionHint: "coppa italia",
  },

  // Germany
  {
    id: "BUNDESLIGA",
    label: "Bundesliga",
    countryHints: ["tyskland", "germany"],
    competitionHint: "bundesliga",
    excludes: ["2. bundesliga", "2.bundesliga", "2 bundesliga", "3. liga", "3.liga", "regionalliga"],
  },
  {
    id: "DFB_POKAL",
    label: "DFB-Pokal",
    countryHints: ["tyskland", "germany"],
    competitionHint: "dfb-pokal",
    alternateCompetitionHints: ["dfb pokal"],
  },

  // France
  {
    id: "LIGUE_1",
    label: "Ligue 1",
    countryHints: ["frankrike", "france"],
    competitionHint: "ligue 1",
    excludes: ["ligue 2"],
  },
  {
    id: "COUPE_DE_FRANCE",
    label: "Coupe de France",
    countryHints: ["frankrike", "france"],
    competitionHint: "coupe de france",
  },

  // Netherlands
  {
    id: "EREDIVISIE",
    label: "Eredivisie",
    countryHints: ["nederland", "holland", "netherlands"],
    competitionHint: "eredivisie",
    excludes: ["eerste"],
  },

  // Portugal
  {
    id: "PRIMEIRA_LIGA",
    label: "Primeira Liga",
    countryHints: ["portugal"],
    competitionHint: "primeira liga",
    alternateCompetitionHints: ["liga portugal"],
    excludes: ["segunda", "liga 3"],
  },

  // Scotland
  {
    id: "SCO_PREMIERSHIP",
    label: "Scottish Premiership",
    countryHints: ["skottland", "scotland", "skotsk"],
    competitionHint: "premiership",
    excludes: ["championship", "league one", "league two"],
  },

  // Norway
  {
    id: "ELITESERIEN",
    label: "Eliteserien",
    countryHints: ["norge", "norway"],
    competitionHint: "eliteserien",
    excludes: ["obos-ligaen"],
  },
  {
    id: "NM_CUP",
    label: "Norgesmesterskapet",
    countryHints: ["norge", "norway"],
    competitionHint: "norgesmesterskapet",
  },

  // UEFA
  {
    id: "UCL",
    label: "Champions League",
    countryHints: ["uefa", "internasjonal", "europa"],
    competitionHint: "champions league",
  },
  {
    id: "UEL",
    label: "Europa League",
    countryHints: ["uefa", "internasjonal", "europa"],
    competitionHint: "europa league",
    excludes: ["conference"],
  },
  {
    id: "UECL",
    label: "Conference League",
    countryHints: ["uefa", "internasjonal", "europa"],
    competitionHint: "conference league",
  },
  {
    id: "UEFA_NATIONS_LEAGUE",
    label: "Nations League",
    countryHints: ["uefa", "internasjonal", "europa", "landslag"],
    competitionHint: "nations league",
  },
  {
    id: "UEFA_SUPER_CUP",
    label: "UEFA Super Cup",
    countryHints: ["uefa", "internasjonal", "europa"],
    competitionHint: "uefa super cup",
  },
  {
    id: "EUROS",
    label: "European Championship",
    countryHints: ["uefa", "internasjonal", "europa", "landslag"],
    competitionHint: "european championship",
    alternateCompetitionHints: ["europamesterskapet", "em-kvalik", "em sluttspill"],
  },

  // FIFA / Confederations
  {
    id: "WORLD_CUP",
    label: "World Cup",
    countryHints: ["fifa", "internasjonal", "verden", "vm", "landslag", "world"],
    competitionHint: "world cup",
    alternateCompetitionHints: ["verdensmesterskapet", "vm-kvalik", "vm sluttspill"],
    excludes: ["club world cup"],
  },
  {
    id: "COPA_AMERICA",
    label: "Copa America",
    countryHints: ["internasjonal", "conmebol", "sør-amerika", "sor-amerika", "south america", "landslag"],
    competitionHint: "copa america",
  },
  {
    id: "AFCON",
    label: "Africa Cup of Nations",
    countryHints: ["internasjonal", "afrika", "africa", "caf", "landslag"],
    competitionHint: "africa cup of nations",
    alternateCompetitionHints: ["afrikamesterskapet"],
  },
];

const WHITELIST_BY_ID: Record<LeagueId, LeaguePattern> = Object.fromEntries(
  WHITELIST.map((p) => [p.id, p] as const),
) as Record<LeagueId, LeaguePattern>;

/** Normalise a string for matching: lowercase + accent-stripped. */
function normalise(raw: string): string {
  return raw.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

/**
 * Match a raw league string against the whitelist. Returns the matched
 * pattern or `null` if the league is not whitelisted. The match is
 * deterministic — first pattern wins, in array order.
 */
export function matchLeague(raw: string | undefined | null): LeaguePattern | null {
  if (!raw) return null;
  const lower = normalise(raw);

  for (const excluded of UNIVERSAL_EXCLUDES) {
    if (lower.includes(excluded)) return null;
  }

  for (const pattern of WHITELIST) {
    const countryOk = pattern.countryHints.some((c) => lower.includes(c));
    if (!countryOk) continue;

    const compHints = [pattern.competitionHint, ...(pattern.alternateCompetitionHints ?? [])];
    const compOk = compHints.some((c) => lower.includes(c));
    if (!compOk) continue;

    const noExclude = !(pattern.excludes ?? []).some((x) => lower.includes(x));
    if (!noExclude) continue;

    return pattern;
  }
  return null;
}

/** Convenience: true if the league matches any whitelist pattern. */
export function isWhitelisted(raw: string | undefined | null): boolean {
  return matchLeague(raw) !== null;
}

/** Look up a pattern by id. Useful for the API query-param filter. */
export function leagueById(id: string): LeaguePattern | null {
  return (WHITELIST_BY_ID as Record<string, LeaguePattern | undefined>)[id] ?? null;
}

/** Whitelist ids in stable order. */
export const ALL_LEAGUE_IDS: ReadonlyArray<LeagueId> = WHITELIST.map((p) => p.id);
